import { ChevronDown, ChevronRight, RefreshCw, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'

import { useRescanSafety } from '@/api/endpoints/safety'
import { useVideos, type VideoItem } from '@/api/endpoints/videos'
import { Button } from '@/components/ui/Button'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { SafetyDetailPanel } from '@/features/safety/components/SafetyDetailPanel'
import { layerTitle } from '@/features/safety/lib/i18n'
import { cn } from '@/lib/cn'
import { preservingChannel } from '@/lib/routing'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import styles from './SafetyPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

/**
 * Bulk view of every video's safety verdict for the active channel.
 *
 * Reads from ``/api/v1/videos`` (which already carries a slim
 * ``video.safety`` blob per item) instead of calling ``/safety/{id}`` N
 * times. Grouping happens client-side: the filter chips switch which
 * subset is rendered. Rescans still fire the job-backed endpoint; the
 * global JobsWatcher toasts on completion.
 */
export function SafetyPage() {
  const { active, isLoading: channelsLoading } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  // High limit so we evaluate the whole channel. The Projects page already
  // paginates — this view's purpose is to find bad apples across the whole
  // set, so we accept the larger payload.
  const { data, isPending, isError, error, refetch } = useVideos({ channel, page: 1, limit: 200 })

  const [filter, setFilter] = useState<SafetyFilter>('all')
  const rescan = useRescanSafety()

  if (channelsLoading || isPending) {
    return (
      <div className={styles.page}>
        <Header />
        <SafetySkeleton />
      </div>
    )
  }
  if (!channel) {
    return (
      <div className={styles.page}>
        <Header />
        <Section>
          <EmptyState title="Sem canal selecionado" description="Escolha um canal no sidebar." />
        </Section>
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.page}>
        <Header />
        <Section>
          <QueryErrorNotice
            error={error}
            label="os vídeos"
            onRetry={() => {
              void refetch()
            }}
          />
        </Section>
      </div>
    )
  }

  const videos = data.videos
  const groups = groupByStatus(videos)
  const visible = filter === 'all' ? videos : groups[filter]
  const neverChecked = groups.never_checked

  return (
    <div className={styles.page}>
      <Header channel={active?.name ?? channel} />

      <div className={styles.summary}>
        <SummaryTile
          label="Total"
          count={videos.length}
          filter="all"
          current={filter}
          onClick={setFilter}
        />
        <SummaryTile
          label="Seguro"
          count={groups.passed.length}
          filter="passed"
          tone="success"
          current={filter}
          onClick={setFilter}
        />
        <SummaryTile
          label="Sem scan"
          count={groups.never_checked.length}
          filter="never_checked"
          tone="warn"
          current={filter}
          onClick={setFilter}
        />
        <SummaryTile
          label="Bloqueado"
          count={groups.blocked.length}
          filter="blocked"
          tone="danger"
          current={filter}
          onClick={setFilter}
        />
        <SummaryTile
          label="Erro / Pendente"
          count={groups.error.length + groups.pending.length}
          filter="error"
          tone="danger"
          current={filter}
          onClick={setFilter}
        />
      </div>

      {neverChecked.length > 0 ? (
        <div className={styles.bulkBar}>
          <span>
            {neverChecked.length} vídeo{neverChecked.length === 1 ? '' : 's'} sem scan neste canal.
          </span>
          <Button
            size="sm"
            variant="secondary"
            leadingIcon={<RefreshCw />}
            disabled={rescan.isPending}
            onClick={() => {
              // Fire rescans in parallel — the jobs endpoint handles
              // concurrency (serializes the safety worker internally).
              for (const v of neverChecked) {
                rescan.mutate({ videoId: v.id, lang: v.language })
              }
            }}
          >
            Rescanear todos sem scan
          </Button>
        </div>
      ) : null}

      <Section
        title={filterLabel(filter)}
        description={`${String(visible.length)} vídeo${visible.length === 1 ? '' : 's'}`}
      >
        {visible.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck />}
            title="Nada aqui"
            description="Nenhum vídeo no estado filtrado."
          />
        ) : (
          <>
            <div className={styles.header}>
              <span aria-hidden />
              <span>Status</span>
              <span>Vídeo</span>
              <span>Verificado</span>
              <span />
            </div>
            <div className={styles.list}>
              {visible.map((v) => (
                <VideoRow
                  key={`${v.id}-${v.language}`}
                  video={v}
                  onRescan={() => {
                    rescan.mutate({ videoId: v.id, lang: v.language })
                  }}
                  rescanPending={rescan.isPending}
                />
              ))}
            </div>
          </>
        )}
      </Section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function SafetySkeleton() {
  // Mirrors the loaded layout — 5 summary tiles, then a section card with
  // a header and 6 row placeholders. Same shimmer used by Ranking/Analytics
  // so the loading "feel" stays uniform across the app.
  const tiles = 5
  const rows = 6
  return (
    <div aria-busy aria-live="polite">
      <div className={styles.summary}>
        {Array.from({ length: tiles }).map((_, i) => (
          <div key={i} className={cn(styles.skeletonTile, styles.skeletonShimmer)} />
        ))}
      </div>
      <div className={styles.skeletonSection} style={{ marginTop: 'var(--space-6)' }}>
        <div className={cn(styles.skeletonSectionHead, styles.skeletonShimmer)} />
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className={styles.skeletonRow}>
            <div />
            <div className={cn(styles.skeletonChip, styles.skeletonShimmer)} />
            <div className={styles.skeletonStack}>
              <div
                className={cn(
                  styles.skeletonLine,
                  styles.skeletonLineWide,
                  styles.skeletonShimmer,
                )}
              />
              <div
                className={cn(
                  styles.skeletonLine,
                  styles.skeletonLineNarrow,
                  styles.skeletonShimmer,
                )}
              />
            </div>
            <div
              className={cn(
                styles.skeletonLine,
                styles.skeletonLineNarrow,
                styles.skeletonShimmer,
              )}
            />
            <div className={cn(styles.skeletonBtn, styles.skeletonShimmer)} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({ channel }: { channel?: string } = {}) {
  return (
    <PageHeader
      eyebrow="Segurança"
      title="Safety"
      description={
        channel
          ? `Verdicts do filtro de segurança para ${channel}. Agrupa vídeos por status e permite rescanear em lote.`
          : 'Verdicts do filtro de segurança.'
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Summary tiles
// ---------------------------------------------------------------------------

type SafetyFilter = 'all' | 'passed' | 'never_checked' | 'blocked' | 'error' | 'pending'

interface SummaryTileProps {
  label: string
  count: number
  filter: SafetyFilter
  current: SafetyFilter
  onClick: (f: SafetyFilter) => void
  tone?: 'success' | 'warn' | 'danger'
}

function SummaryTile({ label, count, filter, current, onClick, tone }: SummaryTileProps) {
  return (
    <button
      type="button"
      className={cn(styles.tile, current === filter && styles.tileActive)}
      onClick={() => { onClick(filter) }}
    >
      <span className={styles.tileLabel}>{label}</span>
      <span
        className={cn(
          styles.tileCount,
          tone === 'success' && styles.tileCountSuccess,
          tone === 'warn' && styles.tileCountWarn,
          tone === 'danger' && styles.tileCountDanger,
        )}
      >
        {count}
      </span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Video row
// ---------------------------------------------------------------------------

interface VideoRowProps {
  video: VideoItem
  onRescan: () => void
  rescanPending: boolean
}

function VideoRow({ video, onRescan, rescanPending }: VideoRowProps) {
  const [open, setOpen] = useState(false)
  const status = video.safety?.status ?? 'never_checked'
  const blockingLayer = video.safety?.blocking_layer ?? null
  const checked = video.safety?.checked_at ? new Date(video.safety.checked_at).toLocaleString('pt-BR') : '—'
  const chip = chipFor(status)
  const title = video.metadata?.title || video.meta_title || video.id
  const Caret = open ? ChevronDown : ChevronRight
  // No verdict row exists yet → nothing to expand. Skip the toggle so the
  // user doesn't open an empty panel.
  const expandable = status !== 'never_checked'
  const summary = rowSummary(status, blockingLayer)

  return (
    <div className={styles.rowWrap}>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.expandToggle}
          aria-expanded={open}
          aria-label={open ? 'Recolher detalhes' : 'Expandir detalhes'}
          disabled={!expandable}
          onClick={() => { setOpen((prev) => !prev) }}
        >
          {expandable ? <Caret size={16} /> : <span aria-hidden>·</span>}
        </button>
        <Chip variant={chip.variant}>{chip.label}</Chip>
        <div className={styles.body}>
          <Link
            to="/projects/$projectId"
            params={{ projectId: video.id }}
            search={preservingChannel({ lang: video.language })}
            className={styles.title}
          >
            {title}
          </Link>
          <span className={styles.reason}>{summary}</span>
        </div>
        <span className={styles.checkedCell}>{checked}</span>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<RefreshCw />}
          disabled={rescanPending}
          onClick={onRescan}
        >
          Rescanear
        </Button>
      </div>
      {open && expandable ? (
        <div className={styles.rowDetail}>
          <SafetyDetailPanel videoId={video.id} lang={video.language} />
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface GroupedVideos {
  passed: VideoItem[]
  blocked: VideoItem[]
  error: VideoItem[]
  pending: VideoItem[]
  never_checked: VideoItem[]
}

function groupByStatus(videos: VideoItem[]): GroupedVideos {
  // O(n) and called once per render — memoisation would be noise. If the
  // list grows past a few hundred rows and the filter toggling feels
  // sluggish, lift this into a ``useMemo`` at the call site.
  const out: GroupedVideos = {
    passed: [],
    blocked: [],
    error: [],
    pending: [],
    never_checked: [],
  }
  for (const v of videos) {
    const s = v.safety?.status ?? 'never_checked'
    if (s === 'passed') out.passed.push(v)
    else if (s === 'blocked') out.blocked.push(v)
    else if (s === 'error') out.error.push(v)
    else if (s === 'pending') out.pending.push(v)
    else out.never_checked.push(v)
  }
  return out
}

function filterLabel(f: SafetyFilter): string {
  switch (f) {
    case 'all':
      return 'Todos os vídeos'
    case 'passed':
      return 'Seguros'
    case 'never_checked':
      return 'Sem scan'
    case 'blocked':
      return 'Bloqueados'
    case 'error':
      return 'Com erro'
    case 'pending':
      return 'Pendentes'
  }
}

function rowSummary(status: string, blockingLayer: string | null): string {
  switch (status) {
    case 'passed':
      return 'Tudo certo: nenhuma camada bloqueou.'
    case 'blocked':
      return blockingLayer
        ? `Bloqueado pela camada ${layerTitle(blockingLayer)}.`
        : 'Bloqueado pelo scan.'
    case 'needs_review':
      return 'Passou no scan, mas o tema pediu revisão humana.'
    case 'error':
      return 'O scan não terminou. Rode um rescan.'
    case 'pending':
      return 'Scan em andamento.'
    case 'never_checked':
      return 'Sem scan ainda. Clique em Rescanear.'
    default:
      return ''
  }
}

function chipFor(status: string): { label: string; variant: ChipVariant } {
  switch (status) {
    case 'passed':
      return { label: 'Seguro', variant: 'success' }
    case 'blocked':
      return { label: 'Bloqueado', variant: 'danger' }
    case 'error':
      return { label: 'Erro', variant: 'danger' }
    case 'pending':
      return { label: 'Pendente', variant: 'warning' }
    case 'never_checked':
      return { label: 'Sem scan', variant: 'warning' }
    default:
      return { label: status, variant: 'default' }
  }
}
